import type { Step } from "ui";

import { AsyncOperationState } from "fond/async/redux";
import { POLYGON_UPLOAD_LAYER_ID } from "fond/project/polygon";
import { PolygonState } from "fond/project/polygon/redux";
import type { BaseDataTaskRecord, BaseDataTaskRecords, TaskResultLine } from "fond/redux/baseDataTasks";
import type { ProjectState, UploadType } from "fond/types";

import type { TaskPhase } from "./flyout/TaskProgress";
import { BASE_DATA_LAYER_KEYS, BaseDataLayerKey, BaseDataOption, GenerationKey } from "./layers";
import { BaseDataTaskSource, formatStepLabel, getGenerationPhase, getUploadPhase, TASK_STAGES, TaskStage } from "./taskPhases";

export type BaseDataTaskKind = BaseDataTaskSource | "generate";

/** A long-running task writing to base data rows: running, or how it ended. */
export type BaseDataTask = {
  kind: BaseDataTaskKind;
  /** Base data rows the task writes to. */
  keys: BaseDataLayerKey[];
} & (
  | { status: "running"; stage: TaskStage | null; phase: TaskPhase }
  | { status: "succeeded"; result: TaskResultLine[] }
  | { status: "failed"; error: string | null }
);

/** A task of each base data row that has one. */
export type BaseDataTasks = Partial<Record<BaseDataLayerKey, BaseDataTask>>;

export type BaseDataTaskInput = {
  /** Uploads of the current project, keyed by upload id. */
  uploads: Record<string, UploadType | null | undefined> | undefined;
  polygon: Pick<ProjectState["polygon"], "isOpen" | "polygonState" | "downloadState" | "isTooBig">;
};

type BaseDataTaskKindDefinition = {
  /** Verb of the row caption while it runs, e.g. "Uploading". */
  verb: string;
  /** Row caption once it failed. */
  failedCaption: string;
  /** Whether it imports FOND data. */
  isImport: boolean;
  /** The option showing its progress, which stays available while it runs; null when no option shows it. */
  option: BaseDataOption | null;
};

const TASK_KINDS: Record<BaseDataTaskKind, BaseDataTaskKindDefinition> = {
  upload: { verb: "Uploading", failedCaption: "Upload failed", isImport: false, option: "upload" },
  boundaryImport: { verb: "Importing", failedCaption: "Import failed", isImport: true, option: "importLayer" },
  drawnAreaImport: { verb: "Importing", failedCaption: "Import failed", isImport: true, option: "importLayer" },
  uploadedAreaImport: { verb: "Importing", failedCaption: "Import failed", isImport: true, option: "importLayer" },
  boundaryUpload: { verb: "Uploading", failedCaption: "Upload failed", isImport: false, option: "upload" },
  boundarySave: { verb: "Saving", failedCaption: "Save failed", isImport: false, option: null },
  delete: { verb: "Deleting", failedCaption: "Delete failed", isImport: false, option: null },
  generate: { verb: "Generating", failedCaption: "Generation failed", isImport: false, option: null },
};

const UPLOAD_STAGES: TaskStage[] = ["uploadingFiles", "uploading"];

/** Whether a task imports FOND data. */
export const isImportTask = (task: BaseDataTask | null | undefined): task is BaseDataTask => !!task && TASK_KINDS[task.kind].isImport;

/** Return the option showing a kind of task's progress, which stays available while it runs, or null when no option shows it. */
export const getTaskKindOption = (kind: BaseDataTaskKind): BaseDataOption | null => TASK_KINDS[kind].option;

/**
 * Return the stage of a task reading a polygon file and then, for an area import, downloading FOND data inside it, or null while it waits
 * on the user (to confirm the layers to import, or after the file or download failed).
 */
const getPolygonFileStage = (source: BaseDataTaskSource, { uploads, polygon }: BaseDataTaskInput): TaskStage | null => {
  if (!polygon.isOpen) return null;
  if (polygon.polygonState === PolygonState.complete) {
    // Without a download, the task moves on as soon as the file has been read.
    if (!TASK_STAGES[source].includes("downloading")) return "readingFile";
    return polygon.downloadState === AsyncOperationState.executing && !polygon.isTooBig ? "downloading" : null;
  }
  const fileUpload = uploads?.[POLYGON_UPLOAD_LAYER_ID];
  return fileUpload && fileUpload.status !== AsyncOperationState.failure ? "readingFile" : null;
};

/** Return the phase of a running task's stage: its transfer percent while it sends or reads a file, else indeterminate. */
const getRunningPhase = (stage: TaskStage, upload: UploadType | null | undefined): TaskPhase => {
  if (UPLOAD_STAGES.includes(stage)) return getUploadPhase(upload);
  if (stage === "readingFile" && upload && !upload.uploadComplete && upload.percent !== undefined) return { percent: upload.percent };
  return {};
};

/** Return a task as it stands, or null while it waits on the user. */
export const getBaseDataTask = (record: BaseDataTaskRecord, input: BaseDataTaskInput): BaseDataTask | null => {
  const { source: kind, keys, status } = record;
  if (status === "succeeded") return { kind, keys, status, result: record.result ?? [] };
  if (status === "failed") return { kind, keys, status, error: record.error ?? null };

  const stage = record.stage === "readingFile" ? getPolygonFileStage(kind, input) : record.stage;
  if (!stage) return null;
  const upload = input.uploads?.[stage === "readingFile" ? POLYGON_UPLOAD_LAYER_ID : (record.uploadId ?? "")];
  return { kind, keys, status, stage, phase: getRunningPhase(stage, upload) };
};

/** Return the task of each base data row whose flyout started one, keyed by that row. */
export const getOwnedBaseDataTasks = (records: BaseDataTaskRecords, input: BaseDataTaskInput): BaseDataTasks =>
  Object.fromEntries(
    BASE_DATA_LAYER_KEYS.flatMap((key) => {
      const record = records[key];
      const task = record ? getBaseDataTask(record, input) : null;
      return task ? [[key, task]] : [];
    })
  );

/**
 * Return the task each base data row shows: a running task writing to it, preferring the row's own, or else the row's own failed task.
 */
export const getBaseDataRowTasks = (owned: BaseDataTasks): BaseDataTasks =>
  Object.fromEntries(
    BASE_DATA_LAYER_KEYS.flatMap((key) => {
      const own = owned[key];
      const writing = [own, ...Object.values(owned)].find((task) => task?.status === "running" && task.keys.includes(key));
      const task = writing ?? (own?.status === "failed" ? own : undefined);
      return task ? [[key, task]] : [];
    })
  );

/** Return the base data rows with a running task: the row that started it and the rows it writes to. */
export const getBusyBaseDataKeys = (owned: BaseDataTasks): BaseDataLayerKey[] =>
  BASE_DATA_LAYER_KEYS.filter((key) =>
    BASE_DATA_LAYER_KEYS.some((owner) => {
      const task = owned[owner];
      return task?.status === "running" && (owner === key || task.keys.includes(key));
    })
  );

/**
 * Return the FOND data import shown in a row's flyout for some of its layers: the row's own, or else one running from another row that
 * writes to any of them.
 */
export const getImportTaskFor = (owned: BaseDataTasks, key: BaseDataLayerKey, keys: readonly BaseDataLayerKey[]): BaseDataTask | null => {
  const own = owned[key];
  if (isImportTask(own)) return own;
  return (
    Object.values(owned).find(
      (task): task is BaseDataTask => isImportTask(task) && task.status === "running" && task.keys.some((taskKey) => keys.includes(taskKey))
    ) ?? null
  );
};

/** Return the generation task of a running generation workflow. */
export const getGenerationTask = (steps: readonly Step[], key: GenerationKey): BaseDataTask => ({
  kind: "generate",
  keys: [key],
  status: "running",
  stage: null,
  phase: getGenerationPhase(steps, key),
});

/** Return the generation task of each base data row being generated. */
export const getBaseDataGenerationTasks = (running: readonly GenerationKey[], steps: readonly Step[]): BaseDataTasks =>
  Object.fromEntries(running.map((key) => [key, getGenerationTask(steps, key)]));

/** Return the one-line row caption of a task, e.g. "Uploading · 62%", "(2/3) Generating" or "Upload failed"; empty once it succeeded. */
export const getTaskCaption = (task: BaseDataTask): string => {
  if (task.status === "failed") return TASK_KINDS[task.kind].failedCaption;
  if (task.status === "succeeded") return "";
  const { percent, step } = task.phase;
  const { verb } = TASK_KINDS[task.kind];
  if (percent !== undefined) return `${verb} · ${Math.round(percent)}%`;
  return step ? formatStepLabel(verb, step) : verb;
};
